<template lang="pug">
.factoring-contract-list.animated.fadeIn
  .card
    nav.card-header
      b-nav(card-header tabs)
        b-nav-item(:to = '{ name: "FactoringContractList" }') {{ $t('menu:factoringContracts') }}
        b-nav-item(:to = '{ name: "FactoringInvoiceList" }') {{ $t('menu:factoringInvoices') }}
        b-nav-text.ml-auto
          span.toolbar
            router-link.py-0.btn.btn-link(:to = '{ name: "FactoringNew" }') {{ $t('newLink') }}
    .card-body
      form.form-horizontal
        .row
          .form-group.col-lg-6
            .input-group
              .input-group-prepend: span.input-group-text: i.fa.fa-search
              input.form-control(
                v-model.trim = 'searchParams.criteria'
                :placeholder = '$t("searchPlaceholder")'
                type         = 'text'
              )
          .form-group.col-lg-6
            range-input-date(
              :options     = 'rangeInputDateOptions'
              :start.sync  = 'searchParams.dateMin'
              :end.sync    = 'searchParams.dateMax'
              :column.sync = 'searchParams.dateColumn'
            )
        .row
          .form-group.col-lg-6
            range-input-text(
              :options     = 'rangeInputTextOptions'
              :start.sync  = 'searchParams.amountMin'
              :end.sync    = 'searchParams.amountMax'
              :column.sync = 'searchParams.amountColumn'
            )
          .form-group.col-lg-3
            .input-group
              .input-group-prepend: span.input-group-text: i.fa.fa-tag
              fi-select(
                v-model.number = 'searchParams.productId'
                :options       = 'optionsFromAllProducts'
                :placeholder   = '$t("productsPlaceholder")'
              )
          .form-group.col-lg-3
            .input-group
              .input-group-prepend: span.input-group-text: i.fa.fa-flag
              fi-multiselect(
                v-model      = 'statusSearchModel'
                :options     = 'statusOptions'
                :placeholder = '$t("statusesPlaceholder")'
                multiple
              )
      .my-2(v-if = 'factoringContractsTotal') {{ $t('searchResults', { results: factoringContractsTotal }) }}
      fi-table(
        :fields      = 'fields'
        :items       = 'factoringContracts'
        loader       = 'factoring:contracts:fetch'
        :empty-label = '$t("notFound")'
        :sort.sync   = 'searchParams.sort'
        responsive
      )
        template(#default = '{ items }')
          factoring-contract-list-item.animated.fadeIn(
            v-for     = 'factoring in items'
            :key      = 'factoring.id'
            :contract = 'factoring'
          )
      fi-pagination(
        v-if    = 'factoringContractsPages > 1'
        :pages  = 'factoringContractsPages'
        v-model = 'pagination.page'
      )
</template>


<script>
import pick from 'lodash/pick'
import { pagination } from '@/const'
import pickBy from 'lodash/pickBy'
import debounce from 'lodash/debounce'
import RangeInputDate from '@/components/RangeInputDate'
import RangeInputText from '@/components/RangeInputText'
import FiSelect from '@/components/FiSelect'
import FiTable from '@/components/FiTable'
import FiPagination from '@/components/FiPagination'
import { mapActions, mapGetters, mapState } from 'vuex'
import FactoringContractListItem from '@/views/factoring/FactoringContractListItem'
import FiMultiselect from '@/components/FiMultiselect'
import { getStatuesQuery } from '@/helpers'

const searchParams = {
  criteria: '',
  dateColumn: 'periodStart',
  dateMin: null,
  dateMax: null,
  amountColumn: 'balance',
  amountMin: null,
  amountMax: null,
  productId: '',
  statuses: [],
  sort: 'factoringId,desc'
}

export default {
  name: 'factoring-contract-list',
  components: { FiMultiselect, FactoringContractListItem, FiPagination, FiTable, FiSelect, RangeInputText, RangeInputDate },
  data: (vm) => ({
    searchParams: { ...searchParams, ...pick(vm.$route.query, Object.keys(searchParams)), statuses: getStatuesQuery(vm.$route.query.statuses), searchQuery: vm.criteria },
    pagination: { ...pagination, ...pick(vm.$route.query, Object.keys(pagination)) }
  }),

  computed: {
    ...mapState('factoring', ['factoringContracts', 'factoringContractsPages', 'factoringContractsTotal']),
    ...mapGetters('classifiers', ['optionsFromClassifier']),
    ...mapGetters('products', ['optionsFromAllProducts']),
    statusOptions () {
      return this.optionsFromClassifier('CreditContractStatus')
    },
    statusSearchModel: {
      get () {
        return this.searchParams.statuses.map(searchValue => this.statusOptions.find(({ value }) => value === searchValue))
      },
      set (values) {
        if (values.length === this.statusOptions.length) {
          this.searchParams.statuses = []
        } else {
          this.searchParams.statuses = values.map(({ value }) => value)
        }
      }
    },
    searchQuery () {
      return pickBy({ ...this.searchParams, ...this.pagination })
    },
    rangeInputDateOptions () {
      return {
        periodStart: this.$t('startDatePlaceholder'),
        periodEnd: this.$t('endDatePlaceholder'),
        nextPaymentDate: this.$t('nextPaymentDate')
      }
    },
    rangeInputTextOptions () {
      return {
        balance: this.$t('balance'),
        contractLimit: this.$t('limit'),
        debts: this.$t('debt')
      }
    },
    fields () {
      return [
        {
          key: 'contract',
          label: this.$t('contract'),
          sort: 'agreementNumber'
        },
        {
          key: 'customer',
          label: this.$t('customer'),
          sort: 'customerFullName'
        },
        {
          key: 'product',
          label: this.$t('product'),
          sort: 'productId'
        },
        {
          key: 'balance',
          label: this.$t('balance'),
          class: 'money'
        },
        {
          key: 'limit',
          label: this.$t('limit'),
          class: 'money'
        },
        {
          key: 'debt',
          label: this.$t('debt'),
          class: 'money',
          sort: 'debts'
        },
        {
          key: 'reference',
          label: this.$t('reference')
        },
        {
          key: 'periodStart',
          label: this.$t('startDatePlaceholder'),
          sort: 'periodStart'
        },
        {
          key: 'endDate',
          label: this.$t('endDatePlaceholder'),
          sort: 'periodEnd'
        },
        {
          key: 'nextPaymentDate',
          label: this.$t('nextPaymentDate')
        },
        {
          key: 'signingDate',
          label: this.$t('signingDate')
        },
        {
          key: 'manager',
          label: this.$t('manager')
        },
        {
          key: 'status',
          label: this.$t('status'),
          sort: 'statusId'
        }
      ]
    },
    searchFilter () {
      const filter = { ...this.searchParams }
      if (!(filter.dateMin || filter.dateMax)) {
        filter.dateColumn = null
      }
      if (!(filter.amountMin || filter.amountMax)) {
        filter.amountColumn = null
      }
      return filter
    }
  },

  watch: {
    type: 'search',
    'pagination.page': 'search',
    searchParams: {
      handler () {
        this.pagination.page = 0
        this.search()
      },
      deep: true
    },
    '$route.query': {
      handler (query) {
        this.loadFactoringContracts({ filters: { ...this.searchQuery, ...query } })
      },
      deep: true,
      immediate: true
    }
  },

  created () {
    this.loadFactoringClassifiers()
    this.loadManagers()
    this.loadProducts({ group: 'FACTORING' })
  },

  methods: {
    ...mapActions('factoring', ['loadFactoringContracts']),
    ...mapActions('classifiers', ['loadFactoringClassifiers', 'loadManagers']),
    ...mapActions('products', ['loadProducts']),
    search: debounce(
      function () {
        this.$router.push({ query: this.searchQuery })
      }, 250
    )
  }
}
</script>


<i18n>
en:
  newLink:              "+ New factoring"
  searchPlaceholder:    "Search by name, id code, agreement reference"
  startDatePlaceholder: "Start Date"
  endDatePlaceholder:   "End Date"
  productsPlaceholder:  "All products"
  statusesPlaceholder:  "All statuses"
  nextPaymentDate:      "Next payment date"
  customer:             "Customer"
  contract:             "Contract"
  amount:               "Amount"
  balance:              "Balance"
  limit:                "Contract limit"
  debt:                 "Debt"
  reference:            "Reference"
  product:              "Product"
  status:               "Status"
  signingDate:          "Sign date"
  manager:              "Manager"
  notFound:             "Contracts not found"
  searchResults:        "Search results: {{ results }}"
et:
  newLink:              "+ New factoring"
  searchPlaceholder:    "Search by name, id code, agreement reference"
  startDatePlaceholder: "Start Date"
  endDatePlaceholder:   "End Date"
  productsPlaceholder:  "All products"
  statusesPlaceholder:  "All statuses"
  nextPaymentDate:      "Next payment date"
  customer:             "Customer"
  contract:             "Contract"
  amount:               "Amount"
  balance:              "Balance"
  limit:                "Contract limit"
  debt:                 "Debt"
  reference:            "Reference"
  product:              "Product"
  status:               "Status"
  signingDate:          "Sign date"
  manager:              "Manager"
  notFound:             "Contracts not found"
  searchResults:        "Search results: {{ results }}"
ru:
  newLink:              "+ Создать факторинг контракт"
  searchPlaceholder:    "Search by name, id code, agreement reference"
  startDatePlaceholder: "Start Date"
  endDatePlaceholder:   "End Date"
  productsPlaceholder:  "All products"
  statusesPlaceholder:  "All statuses"
  nextPaymentDate:      "Next payment date"
  customer:             "Customer"
  contract:             "Contract"
  amount:               "Amount"
  balance:              "Balance"
  limit:                "Contract limit"
  debt:                 "Debt"
  reference:            "Reference"
  product:              "Product"
  status:               "Status"
  signingDate:          "Sign date"
  manager:              "Manager"
  notFound:             "Contracts not found"
  searchResults:        "Search results: {{ results }}"
</i18n>
